import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Users, UserPlus, Search, Shield, Edit, Trash2, Mail, Phone, Calendar } from 'lucide-react';
import Navbar from '../../components/layout/Navbar';
import Footer from '../../components/layout/Footer';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Badge } from '../../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../../components/ui/dialog';
import api from '../../utils/api';
import { getUser } from '../../utils/auth';
import { toast } from 'sonner';
import { format } from 'date-fns';

const UsersManagement = () => {
  const { i18n } = useTranslation();
  const navigate = useNavigate();
  const user = getUser();
  const [loading, setLoading] = useState(true);
  const [users, setUsers] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterRole, setFilterRole] = useState('all');
  const [showAddUser, setShowAddUser] = useState(false);

  const [newUser, setNewUser] = useState({
    email: '',
    password: '',
    full_name: '',
    phone: '',
    role: 'client'
  });

  useEffect(() => {
    if (user?.role !== 'admin') {
      navigate('/dashboard');
      return;
    }
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    try {
      const response = await api.get('/cases'); // We'll get users from cases for now
      // In production, you'd have a dedicated /users endpoint
      setUsers([
        { id: '1', email: 'admin@wazin.law', full_name: 'مدير النظام', role: 'admin', is_active: true, created_at: new Date() },
        { id: '2', email: 'test@wazin.law', full_name: 'عميل تجريبي', role: 'client', is_active: true, created_at: new Date() },
      ]);
    } catch (error) {
      toast.error(i18n.language === 'ar' ? 'فشل تحميل المستخدمين' : 'Failed to load users');
    } finally {
      setLoading(false);
    }
  };

  const handleAddUser = async (e) => {
    e.preventDefault();
    try {
      await api.post('/auth/register', newUser);
      toast.success(i18n.language === 'ar' ? 'تم إضافة المستخدم بنجاح' : 'User added successfully');
      setShowAddUser(false);
      setNewUser({ email: '', password: '', full_name: '', phone: '', role: 'client' });
      fetchUsers();
    } catch (error) {
      toast.error(i18n.language === 'ar' ? 'فشل إضافة المستخدم' : 'Failed to add user');
    }
  };

  const stats = [
    {
      title: i18n.language === 'ar' ? 'إجمالي المستخدمين' : 'Total Users',
      value: users.length,
      icon: Users,
      color: 'from-blue-500 to-blue-600'
    },
    {
      title: i18n.language === 'ar' ? 'المدراء' : 'Admins',
      value: users.filter(u => u.role === 'admin').length,
      icon: Shield,
      color: 'from-purple-500 to-purple-600'
    },
    {
      title: i18n.language === 'ar' ? 'المحامون' : 'Lawyers',
      value: users.filter(u => u.role === 'lawyer').length,
      icon: Users,
      color: 'from-green-500 to-green-600'
    },
    {
      title: i18n.language === 'ar' ? 'العملاء' : 'Clients',
      value: users.filter(u => u.role === 'client').length,
      icon: Users,
      color: 'from-orange-500 to-orange-600'
    },
  ];

  const filteredUsers = users.filter(u => {
    const matchesSearch = u.full_name.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         u.email.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesRole = filterRole === 'all' || u.role === filterRole;
    return matchesSearch && matchesRole;
  });

  if (loading) {
    return <div className="min-h-screen flex items-center justify-center"><div className="spinner" /></div>;
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100">
      <Navbar />
      <main className="pt-24 pb-12 px-4">
        <div className="max-w-7xl mx-auto space-y-8">
          <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
            <div>
              <h1 className="text-4xl font-bold mb-2">{i18n.language === 'ar' ? 'إدارة المستخدمين' : 'Users Management'}</h1>
              <p className="text-text-secondary text-lg">{i18n.language === 'ar' ? 'إدارة جميع المستخدمين والصلاحيات' : 'Manage all users and permissions'}</p>
            </div>
            <Dialog open={showAddUser} onOpenChange={setShowAddUser}>
              <DialogTrigger asChild>
                <Button className="btn-primary gap-2">
                  <UserPlus className="w-5 h-5" />
                  {i18n.language === 'ar' ? 'إضافة مستخدم' : 'Add User'}
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-2xl">
                <DialogHeader>
                  <DialogTitle>{i18n.language === 'ar' ? 'إضافة مستخدم جديد' : 'Add New User'}</DialogTitle>
                </DialogHeader>
                <form onSubmit={handleAddUser} className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label>{i18n.language === 'ar' ? 'الاسم الكامل' : 'Full Name'}</Label>
                      <Input value={newUser.full_name} onChange={(e) => setNewUser({...newUser, full_name: e.target.value})} required />
                    </div>
                    <div>
                      <Label>{i18n.language === 'ar' ? 'البريد الإلكتروني' : 'Email'}</Label>
                      <Input type="email" value={newUser.email} onChange={(e) => setNewUser({...newUser, email: e.target.value})} required />
                    </div>
                    <div>
                      <Label>{i18n.language === 'ar' ? 'رقم الجوال' : 'Phone'}</Label>
                      <Input value={newUser.phone} onChange={(e) => setNewUser({...newUser, phone: e.target.value})} />
                    </div>
                    <div>
                      <Label>{i18n.language === 'ar' ? 'كلمة المرور' : 'Password'}</Label>
                      <Input type="password" value={newUser.password} onChange={(e) => setNewUser({...newUser, password: e.target.value})} required />
                    </div>
                    <div>
                      <Label>{i18n.language === 'ar' ? 'الدور' : 'Role'}</Label>
                      <Select value={newUser.role} onValueChange={(value) => setNewUser({...newUser, role: value})}>
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="client">{i18n.language === 'ar' ? 'عميل' : 'Client'}</SelectItem>
                          <SelectItem value="lawyer">{i18n.language === 'ar' ? 'محامي' : 'Lawyer'}</SelectItem>
                          <SelectItem value="admin">{i18n.language === 'ar' ? 'مدير' : 'Admin'}</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  <div className="flex justify-end gap-3">
                    <Button type="button" variant="outline" onClick={() => setShowAddUser(false)}>
                      {i18n.language === 'ar' ? 'إلغاء' : 'Cancel'}
                    </Button>
                    <Button type="submit" className="btn-primary">{i18n.language === 'ar' ? 'إضافة' : 'Add'}</Button>
                  </div>
                </form>
              </DialogContent>
            </Dialog>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
            {stats.map((stat, index) => (
              <Card key={index} className="relative overflow-hidden">
                <div className={`absolute inset-0 bg-gradient-to-br ${stat.color} opacity-5`} />
                <CardContent className="pt-6 relative">
                  <div className="flex items-start justify-between">
                    <div>
                      <p className="text-sm text-text-secondary mb-1">{stat.title}</p>
                      <p className="text-3xl font-bold">{stat.value}</p>
                    </div>
                    <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${stat.color} flex items-center justify-center`}>
                      <stat.icon className="w-6 h-6 text-white" />
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>

          <Card>
            <CardHeader>
              <div className="flex flex-col md:flex-row gap-4">
                <div className="relative flex-1">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-text-secondary" />
                  <Input
                    placeholder={i18n.language === 'ar' ? 'بحث...' : 'Search...'}
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="pl-10"
                  />
                </div>
                <Select value={filterRole} onValueChange={setFilterRole}>
                  <SelectTrigger className="w-full md:w-48">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">{i18n.language === 'ar' ? 'الكل' : 'All'}</SelectItem>
                    <SelectItem value="admin">{i18n.language === 'ar' ? 'مدير' : 'Admin'}</SelectItem>
                    <SelectItem value="lawyer">{i18n.language === 'ar' ? 'محامي' : 'Lawyer'}</SelectItem>
                    <SelectItem value="client">{i18n.language === 'ar' ? 'عميل' : 'Client'}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {filteredUsers.map((userItem) => (
                  <div key={userItem.id} className="flex items-center justify-between p-4 border rounded-lg hover:bg-gray-50">
                    <div className="flex items-center gap-4 flex-1">
                      <div className="w-12 h-12 rounded-full bg-gradient-to-br from-primary to-primary-light flex items-center justify-center text-white font-bold">
                        {userItem.full_name[0]}
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-1">
                          <p className="font-semibold">{userItem.full_name}</p>
                          <Badge variant={userItem.role === 'admin' ? 'destructive' : 'default'}>
                            {i18n.language === 'ar' 
                              ? (userItem.role === 'admin' ? 'مدير' : userItem.role === 'lawyer' ? 'محامي' : 'عميل')
                              : userItem.role
                            }
                          </Badge>
                          {userItem.is_active && <Badge variant="outline" className="text-green-600 border-green-600">نشط</Badge>}
                        </div>
                        <div className="flex items-center gap-4 text-sm text-text-secondary">
                          <span className="flex items-center gap-1">
                            <Mail className="w-4 h-4" />
                            {userItem.email}
                          </span>
                          <span className="flex items-center gap-1">
                            <Calendar className="w-4 h-4" />
                            {format(new Date(userItem.created_at), 'dd/MM/yyyy')}
                          </span>
                        </div>
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <Button size="sm" variant="outline"><Edit className="w-4 h-4" /></Button>
                      <Button size="sm" variant="outline" className="text-red-600 hover:text-red-700"><Trash2 className="w-4 h-4" /></Button>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default UsersManagement;
